import {
    ApartmentOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    CheckCircleFilled,
    CheckOutlined,
    MailOutlined,
    ManOutlined,
    PhoneOutlined,
    SafetyCertificateOutlined,
    SaveOutlined,
    SearchOutlined,
    UserOutlined,
    WomanOutlined,
} from "@ant-design/icons";
import {
    Avatar,
    Button,
    Card,
    Col,
    Divider,
    Empty,
    Form,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Steps,
    Switch,
    Tag,
    Typography,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";

const { Text, Title } = Typography;
const { confirm } = Modal;
const { Option } = Select;
const { TextArea } = Input;

const AVATAR_COLORS = [
    "#1677ff", "#52c41a", "#fa8c16", "#722ed1",
    "#eb2f96", "#13c2c2", "#f5222d", "#2f54eb",
];
const avatarColor = (str = "") =>
    str ? AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length] : "rgba(255,255,255,0.2)";

const FIELD_LABEL = { color: "#595959", fontWeight: 600, fontSize: 13 };
const REQ = <span style={{ color: "#ff4d4f" }}> *</span>;

const SECTION_LABEL_STYLE = {
    fontSize: 11, fontWeight: 700, color: "#8c8c8c",
    textTransform: "uppercase", letterSpacing: "0.6px",
    marginBottom: 14, display: "flex", alignItems: "center", gap: 6,
};

/* ── Card sélectionnable (profil / agence) ── */
const SelectCard = ({ selected, onClick, icon, title, subtitle, accentColor = "#1677ff", disabled }) => (
    <div
        onClick={disabled ? undefined : onClick}
        style={{
            border: `2px solid ${selected ? accentColor : "#f0f0f0"}`,
            borderRadius: 10,
            padding: "14px 16px",
            cursor: disabled ? "not-allowed" : "pointer",
            background: selected
                ? accentColor === "#1677ff" ? "#f0f7ff" : "#f6ffed"
                : "#fafafa",
            transition: "all 0.2s",
            position: "relative",
            opacity: disabled && !selected ? 0.5 : 1,
        }}
        onMouseEnter={(e) => {
            if (!selected && !disabled)
                e.currentTarget.style.borderColor = `${accentColor}80`;
        }}
        onMouseLeave={(e) => {
            if (!selected)
                e.currentTarget.style.borderColor = "#f0f0f0";
        }}
    >
        {selected && (
            <div style={{
                position: "absolute", top: 8, right: 8,
                background: accentColor, borderRadius: "50%",
                width: 18, height: 18,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <CheckOutlined style={{ color: "white", fontSize: 9 }} />
            </div>
        )}
        <div style={{ fontSize: 20, color: selected ? accentColor : "#8c8c8c", marginBottom: 6 }}>
            {icon}
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#262626" }}>{title}</div>
        {subtitle && (
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 2 }}>{subtitle}</div>
        )}
    </div>
);

/* ══════════════════════════════════════════════ */
const NewAddUtilisateur = () => {
    const [profils, setProfils] = useState([]);
    const [agences, setAgences] = useState([]);
    const [selectedProfil, setSelectedProfil] = useState(null);
    const [selectedAgence, setSelectedAgence] = useState("");
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [mailExists, setMailExists] = useState(false);
    const [myAgence, setMyAgence] = useState(null);
    const [organisme, setOrganisme] = useState(null);
    const [useDomaine, setUseDomaine] = useState(true);
    const [errors, setErrors] = useState({});
    const [profilSearch, setProfilSearch] = useState("");

    // Filtre les profils par NOM de profil OU raison sociale de la compagnie (insensible casse/accents).
    const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    const filteredProfils = useMemo(() => {
        const q = norm(profilSearch).trim();
        if (!q) return profils;
        return profils.filter(
            (p) => norm(p.nom).includes(q) || norm(p.organisme?.raisonSociale).includes(q)
        );
    }, [profils, profilSearch]);

    const [formData, setFormData] = useState({
        nom: "", prenom: "", genre: "", fonction: "",
        adresseResidence: "", telephone: "", email: "", active: true,
    });

    const param = useParams();
    const navigate = useNavigate();
    const isCourtier = myAgence?.typeAgence === "COURTIER";
    // Utilisateur CONNECTÉ courtier : il ne peut créer que dans SA propre agence, avec SON profil.
    const isLoggedCourtier = LOGGED_USER?.agence?.typeAgence === "COURTIER";
    // Contexte courtier pour le DOMAINE : agence cible courtier OU utilisateur connecté courtier.
    // (Séparé de isCourtier qui pilote l'agence/profil au submit et a besoin de myAgence.)
    const isCourtierDomaine = isCourtier || isLoggedCourtier;

    const fullEmail = (useDomaine && !isCourtierDomaine && organisme?.domaine)
        ? `${formData.email}@${organisme.domaine}`
        : formData.email;

    const initials = useMemo(() => {
        const parts = [formData.nom, formData.prenom].filter(Boolean);
        return parts.map((p) => p[0]?.toUpperCase()).join("") || null;
    }, [formData.nom, formData.prenom]);

    const fullName = [formData.prenom, formData.nom].filter(Boolean).join(" ") || "Nouvel utilisateur";

    /* ── Data fetching ── */
    useEffect(() => {
        axiosInstance
            .get(`/api/organisme/code/${LOGGED_USER.profil.organisme.code}`)
            .then((res) => setOrganisme(res.data))
            .catch(() => {});

        if (param?.codeAgence) {
            axiosInstance.get(`/api/agence/code/${param.codeAgence}`).then((res) => {
                loadAgencies(res.data?.organisme?.code);
                axiosInstance
                    .get(`/api/profil/organisme/codeOrganisme/${res.data?.organisme?.code}`)
                    .then((r) => setProfils(r.data))
                    .catch(() => {});
                setSelectedAgence(res.data.codeId);
                setMyAgence(res.data);
            }).catch(() => {});
        } else if (isLoggedCourtier && LOGGED_USER?.profil) {
            // Courtier connecté : SEUL son profil est proposé et SON agence est figée.
            setProfils([LOGGED_USER.profil]);
            setSelectedProfil(LOGGED_USER.profil);
            if (LOGGED_USER.agence) {
                setAgences([LOGGED_USER.agence]);
                setSelectedAgence(LOGGED_USER.agence.codeId);
            }
        } else {
            axiosInstance.get(`/api/profil/used`)
                .then((res) => setProfils(res.data))
                .catch(() => {});
        }
    }, [param.codeAgence]);

    // Contexte courtier : l'utilisateur a une adresse externe → pas de domaine du Pool.
    useEffect(() => {
        if (isCourtierDomaine) setUseDomaine(false);
    }, [isCourtierDomaine]);

    const loadAgencies = (code) => {
        axiosInstance.get(`/api/agence/organisme/code/${code}`).then((res) => {
            setAgences(res.data);
            if (res.data?.length) setSelectedAgence(res.data[0].codeId);
        }).catch(() => {});
    };

    const handleProfilSelect = (codeId) => {
        const prof = profils.find((e) => e.codeId === codeId);
        setSelectedProfil(prof);
        // Courtier connecté : agence figée → on NE recharge PAS la liste des agences.
        if (!isLoggedCourtier) loadAgencies(prof.organisme.code);
    };

    /* ── Validation ── */
    const validateStep1 = () => {
        const newErrors = {};
        const phonePattern = /^(22|90|91|92|93|96|98|99|70|71|79|97)\d{6}$/;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.nom || formData.nom.length < 2)
            newErrors.nom = "Nom requis (min. 2 caractères)";
        if (!formData.prenom || formData.prenom.length < 2)
            newErrors.prenom = "Prénom requis (min. 2 caractères)";
        if (!formData.genre)
            newErrors.genre = "Genre requis";
        if (!formData.telephone || !phonePattern.test(formData.telephone))
            newErrors.telephone = "Numéro invalide (8 chiffres, ex. 90123456)";
        if (!formData.email)
            newErrors.email = "Email requis";
        else if (!emailPattern.test(fullEmail))
            newErrors.email = "Adresse email invalide";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const checkEmailExists = async () => {
        try {
            const res = await axiosInstance.get(`/api/utilisateur/check/${fullEmail}`);
            if (res.data) toast.warning("Un utilisateur avec cet email existe déjà !");
            setMailExists(res.data);
            return res.data;
        } catch {
            return false;
        }
    };

    const handleNext = async () => {
        if (!validateStep1()) return;
        const exists = await checkEmailExists();
        if (!exists) setCurrentStep(1);
    };

    const confirmAndSubmit = () => {
        const ag = agences.find((e) => e.codeId === selectedAgence);
        confirm({
            title: "Confirmer la création",
            width: 480,
            icon: null,
            content: (
                <div style={{ paddingTop: 4 }}>
                    <div
                        style={{
                            background: "linear-gradient(135deg, #242166 0%, #3b82f6 100%)",
                            borderRadius: 10,
                            padding: "16px 20px",
                            marginBottom: 16,
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                        }}
                    >
                        <Avatar
                            size={48}
                            style={{
                                backgroundColor: avatarColor(formData.nom),
                                fontWeight: 700,
                                fontSize: 18,
                                border: "2px solid rgba(255,255,255,0.3)",
                                flexShrink: 0,
                            }}
                        >
                            {[formData.nom, formData.prenom]
                                .filter(Boolean)
                                .map((p) => p[0]?.toUpperCase())
                                .join("")}
                        </Avatar>
                        <div>
                            <div style={{ fontWeight: 700, color: "white", fontSize: 15, lineHeight: 1.3 }}>
                                {[formData.prenom, formData.nom].filter(Boolean).join(" ")}
                            </div>
                            {formData.fonction && (
                                <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, marginTop: 2 }}>
                                    {formData.fonction}
                                </div>
                            )}
                        </div>
                    </div>

                    <Space direction="vertical" size={10} style={{ width: "100%" }}>
                        <ConfirmRow icon={<MailOutlined style={{ color: "#1677ff" }} />} label="Email" value={fullEmail} />
                        <ConfirmRow icon={<PhoneOutlined style={{ color: "#52c41a" }} />} label="Téléphone" value={`+228 ${formData.telephone}`} />
                        {formData.genre && (
                            <ConfirmRow
                                icon={formData.genre === "MASCULIN"
                                    ? <ManOutlined style={{ color: "#1677ff" }} />
                                    : <WomanOutlined style={{ color: "#eb2f96" }} />
                                }
                                label="Genre"
                                value={formData.genre === "MASCULIN" ? "Masculin" : "Féminin"}
                            />
                        )}
                        {selectedProfil && (
                            <ConfirmRow
                                icon={<SafetyCertificateOutlined style={{ color: "#722ed1" }} />}
                                label="Profil"
                                value={`${selectedProfil.nom} — ${selectedProfil.organisme?.raisonSociale}`}
                            />
                        )}
                        {ag && (
                            <ConfirmRow
                                icon={<ApartmentOutlined style={{ color: "#fa8c16" }} />}
                                label="Agence"
                                value={`${ag.nom}${ag.ville ? ` — ${ag.ville}` : ""}`}
                            />
                        )}
                        <div style={{ marginTop: 4 }}>
                            <Tag
                                color={formData.active ? "success" : "default"}
                                style={{ borderRadius: 20, padding: "2px 14px", fontSize: 12 }}
                            >
                                {formData.active ? "✓ Compte actif" : "○ Compte inactif"}
                            </Tag>
                        </div>
                    </Space>

                    <div
                        style={{
                            marginTop: 16,
                            padding: "10px 14px",
                            background: "#fffbe6",
                            borderRadius: 8,
                            border: "1px solid #ffe58f",
                            fontSize: 12,
                            color: "#7c5800",
                        }}
                    >
                        Un email de connexion sera envoyé à l'adresse indiquée après la création.
                    </div>
                </div>
            ),
            okText: "Créer l'utilisateur",
            okButtonProps: {
                style: {
                    background: "linear-gradient(135deg, #242166, #3b82f6)",
                    border: "none",
                    borderRadius: 8,
                    fontWeight: 600,
                },
            },
            cancelText: "Annuler",
            cancelButtonProps: { style: { borderRadius: 8 } },
            onOk: handleSubmit,
        });
    };

    const handleSubmit = async () => {
        if (!selectedProfil && !isCourtier) {
            toast.warning("Veuillez sélectionner un profil.");
            return;
        }
        setLoading(true);
        const ag = agences.find((e) => e.codeId === selectedAgence);
        const user = {
            nom: formData.nom,
            prenom: formData.prenom,
            email: fullEmail,
            contact: [formData.telephone],
            username: fullEmail,
            genre: formData.genre,
            adresse: formData.adresseResidence,
            fonction: formData.fonction,
            typePersonne: "PHYSIQUE",
            agence: isCourtier ? myAgence : ag,
            profil: isCourtier ? null : selectedProfil,
            active: formData.active,
        };
        try {
            await axiosInstance.post(`/api/personne/register`, JSON.stringify(user));
            toast.success("Utilisateur créé avec succès !");
            navigate("/utilisateurs", { replace: true });
        } catch {
            toast.error("Erreur lors de la création de l'utilisateur.");
        } finally {
            setLoading(false);
        }
    };

    const set = (field) => (val) =>
        setFormData((prev) => ({ ...prev, [field]: val }));
    const setE = (field) => (e) =>
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));

    /* ══════════ Render ══════════ */
    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"UTILISATEURS"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div
                            className="container-xxl flex-grow-1 container-p-y"
                            style={{ background: "#f8f9fb", minHeight: "100vh" }}
                        >
                            {/* ── Page header ── */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
                                <Button
                                    type="text"
                                    shape="circle"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => navigate("/utilisateurs")}
                                    style={{ color: "#8c8c8c", flexShrink: 0 }}
                                />
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#262626" }}>
                                        Nouvel utilisateur
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        Créer un compte utilisateur sur la plateforme
                                    </Text>
                                </div>
                            </div>

                            <Row gutter={24} align="stretch">
                                {/* ════════ Left : Preview ════════ */}
                                <Col xs={24} md={7}>
                                    <div style={{ position: "sticky", top: 24 }}>
                                        {/* User preview card */}
                                        <Card
                                            style={{
                                                borderRadius: 16,
                                                border: "none",
                                                overflow: "hidden",
                                                boxShadow: "0 4px 24px rgba(0,0,0,0.09)",
                                            }}
                                            styles={{ body: { padding: 0 } }}
                                        >
                                            {/* Gradient banner */}
                                            <div
                                                style={{
                                                    background: "linear-gradient(135deg, #242166 0%, #3b82f6 100%)",
                                                    padding: "32px 24px 52px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <Avatar
                                                    size={84}
                                                    style={{
                                                        backgroundColor: initials
                                                            ? avatarColor(formData.nom)
                                                            : "rgba(255,255,255,0.2)",
                                                        fontSize: 32,
                                                        fontWeight: 700,
                                                        border: "3px solid rgba(255,255,255,0.35)",
                                                        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
                                                    }}
                                                    icon={!initials && <UserOutlined />}
                                                >
                                                    {initials}
                                                </Avatar>
                                            </div>

                                            {/* Info panel */}
                                            <div style={{ padding: "0 20px 24px", marginTop: -28 }}>
                                                <Card
                                                    style={{
                                                        borderRadius: 12,
                                                        boxShadow: "0 4px 16px rgba(0,0,0,0.09)",
                                                        border: "none",
                                                    }}
                                                    styles={{ body: { padding: "16px 18px" } }}
                                                >
                                                    <div style={{ textAlign: "center", marginBottom: 10 }}>
                                                        <Text
                                                            strong
                                                            style={{
                                                                fontSize: 15,
                                                                display: "block",
                                                                color: "#262626",
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            {fullName}
                                                        </Text>
                                                        {formData.fonction && (
                                                            <Text
                                                                type="secondary"
                                                                style={{ fontSize: 12, display: "block", marginTop: 2 }}
                                                            >
                                                                {formData.fonction}
                                                            </Text>
                                                        )}
                                                    </div>

                                                    <Divider style={{ margin: "8px 0 10px" }} />

                                                    <Space direction="vertical" size={7} style={{ width: "100%" }}>
                                                        {formData.email && (
                                                            <Space size={7}>
                                                                <MailOutlined style={{ color: "#1677ff", fontSize: 12, flexShrink: 0 }} />
                                                                <Text
                                                                    style={{ fontSize: 12, color: "#595959" }}
                                                                    ellipsis={{ tooltip: fullEmail }}
                                                                >
                                                                    {fullEmail}
                                                                </Text>
                                                            </Space>
                                                        )}
                                                        {formData.telephone && (
                                                            <Space size={7}>
                                                                <PhoneOutlined style={{ color: "#52c41a", fontSize: 12 }} />
                                                                <Text style={{ fontSize: 12, color: "#595959" }}>
                                                                    +228 {formData.telephone}
                                                                </Text>
                                                            </Space>
                                                        )}
                                                        {formData.genre && (
                                                            <Space size={7}>
                                                                {formData.genre === "MASCULIN"
                                                                    ? <ManOutlined style={{ color: "#1677ff", fontSize: 12 }} />
                                                                    : <WomanOutlined style={{ color: "#eb2f96", fontSize: 12 }} />
                                                                }
                                                                <Text style={{ fontSize: 12, color: "#595959" }}>
                                                                    {formData.genre === "MASCULIN" ? "Masculin" : "Féminin"}
                                                                </Text>
                                                            </Space>
                                                        )}
                                                        {selectedProfil && (
                                                            <Space size={7}>
                                                                <SafetyCertificateOutlined style={{ color: "#722ed1", fontSize: 12 }} />
                                                                <Text style={{ fontSize: 12, color: "#595959" }}>
                                                                    {selectedProfil.nom}
                                                                </Text>
                                                            </Space>
                                                        )}
                                                        {agences.find((a) => a.codeId === selectedAgence) && (
                                                            <Space size={7}>
                                                                <ApartmentOutlined style={{ color: "#fa8c16", fontSize: 12 }} />
                                                                <Text style={{ fontSize: 12, color: "#595959" }}>
                                                                    {agences.find((a) => a.codeId === selectedAgence)?.nom}
                                                                </Text>
                                                            </Space>
                                                        )}
                                                    </Space>

                                                    <div style={{ marginTop: 12, textAlign: "center" }}>
                                                        <Tag
                                                            color={formData.active ? "success" : "default"}
                                                            style={{ borderRadius: 20, padding: "2px 14px", fontSize: 12 }}
                                                        >
                                                            {formData.active ? "✓ Compte actif" : "○ Compte inactif"}
                                                        </Tag>
                                                    </div>
                                                </Card>
                                            </div>
                                        </Card>

                                        {/* Étapes */}
                                        <Card
                                            style={{
                                                marginTop: 14,
                                                borderRadius: 12,
                                                border: "1px solid #f0f0f0",
                                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                            }}
                                            styles={{ body: { padding: "16px 20px" } }}
                                        >
                                            <Text
                                                style={{
                                                    fontSize: 11, fontWeight: 700, color: "#8c8c8c",
                                                    textTransform: "uppercase", letterSpacing: "0.5px",
                                                    display: "block", marginBottom: 12,
                                                }}
                                            >
                                                Progression
                                            </Text>
                                            <Steps
                                                direction="vertical"
                                                current={currentStep}
                                                size="small"
                                                items={[
                                                    {
                                                        title: (
                                                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                                                                Identité & Contact
                                                            </span>
                                                        ),
                                                        description: (
                                                            <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                                                                Informations personnelles
                                                            </span>
                                                        ),
                                                        icon: currentStep > 0
                                                            ? <CheckCircleFilled style={{ color: "#52c41a" }} />
                                                            : <UserOutlined />,
                                                    },
                                                    {
                                                        title: (
                                                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                                                                Profil & Accès
                                                            </span>
                                                        ),
                                                        description: (
                                                            <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                                                                Droits et affectation
                                                            </span>
                                                        ),
                                                        icon: <SafetyCertificateOutlined />,
                                                    },
                                                ]}
                                            />
                                        </Card>
                                    </div>
                                </Col>

                                {/* ════════ Right : Form ════════ */}
                                <Col xs={24} md={17}>
                                    <Card
                                        style={{
                                            borderRadius: 16,
                                            border: "1px solid #f0f0f0",
                                            boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                                        }}
                                        styles={{ body: { padding: "28px 32px" } }}
                                    >
                                        {/* ─── Étape 1 : Identité & Contact ─── */}
                                        {currentStep === 0 && (
                                            <>
                                                <StepHeader
                                                    icon={<UserOutlined style={{ color: "#1677ff", fontSize: 18 }} />}
                                                    bg="#e6f4ff"
                                                    title="Identité & Contact"
                                                    subtitle="Renseignez les informations personnelles de l'utilisateur"
                                                />

                                                <Form layout="vertical" requiredMark={false} component="div">
                                                    <div style={SECTION_LABEL_STYLE}>
                                                        <UserOutlined /> Identité
                                                    </div>
                                                    <Row gutter={16}>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Nom{REQ}</span>}
                                                                validateStatus={errors.nom ? "error" : ""}
                                                                help={errors.nom}
                                                            >
                                                                <Input
                                                                    placeholder="Ex. : EDORH"
                                                                    value={formData.nom}
                                                                    onChange={setE("nom")}
                                                                    prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                                                                    style={{ borderRadius: 8 }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Prénom(s){REQ}</span>}
                                                                validateStatus={errors.prenom ? "error" : ""}
                                                                help={errors.prenom}
                                                            >
                                                                <Input
                                                                    placeholder="Ex. : Kossi"
                                                                    value={formData.prenom}
                                                                    onChange={setE("prenom")}
                                                                    prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                                                                    style={{ borderRadius: 8 }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Genre{REQ}</span>}
                                                                validateStatus={errors.genre ? "error" : ""}
                                                                help={errors.genre}
                                                            >
                                                                <Select
                                                                    placeholder="Sélectionner"
                                                                    value={formData.genre || undefined}
                                                                    onChange={set("genre")}
                                                                    style={{ borderRadius: 8 }}
                                                                >
                                                                    <Option value="MASCULIN">Masculin</Option>
                                                                    <Option value="FEMININ">Féminin</Option>
                                                                </Select>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Fonction</span>}
                                                            >
                                                                <Input
                                                                    placeholder="Ex. : Directeur technique"
                                                                    value={formData.fonction}
                                                                    onChange={setE("fonction")}
                                                                    style={{ borderRadius: 8 }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>

                                                    <Divider style={{ margin: "4px 0 16px" }} />
                                                    <div style={SECTION_LABEL_STYLE}>
                                                        <PhoneOutlined /> Contact
                                                    </div>

                                                    <Row gutter={16}>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Adresse email{REQ}</span>}
                                                                validateStatus={
                                                                    errors.email ? "error"
                                                                        : mailExists ? "warning"
                                                                            : ""
                                                                }
                                                                help={errors.email || (mailExists ? "Email déjà utilisé" : null)}
                                                            >
                                                                <Input
                                                                    placeholder={
                                                                        useDomaine && !isCourtierDomaine && organisme?.domaine
                                                                            ? "nom.prenom"
                                                                            : "nom@email.com"
                                                                    }
                                                                    value={formData.email}
                                                                    onChange={setE("email")}
                                                                    prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                                                                    addonAfter={
                                                                        useDomaine && !isCourtierDomaine && organisme?.domaine
                                                                            ? `@${organisme.domaine}`
                                                                            : null
                                                                    }
                                                                    style={{ borderRadius: 8 }}
                                                                />
                                                                {/* Restriction de domaine : MASQUÉE en contexte courtier (adresse externe libre). */}
                                                                {organisme?.domaine && !isCourtierDomaine && (
                                                                    <div
                                                                        style={{
                                                                            marginTop: 6,
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        <Switch
                                                                            size="small"
                                                                            checked={useDomaine}
                                                                            onChange={setUseDomaine}
                                                                        />
                                                                        <Text style={{ fontSize: 12, color: "#8c8c8c" }}>
                                                                            Utiliser le domaine @{organisme.domaine}
                                                                        </Text>
                                                                    </div>
                                                                )}
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Téléphone{REQ}</span>}
                                                                validateStatus={errors.telephone ? "error" : ""}
                                                                help={errors.telephone}
                                                            >
                                                                <Input
                                                                    placeholder="90 12 34 56"
                                                                    value={formData.telephone}
                                                                    onChange={setE("telephone")}
                                                                    prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                                                                    addonBefore="🇹🇬 +228"
                                                                    style={{ borderRadius: 8 }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={24}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Adresse de résidence</span>}
                                                            >
                                                                <TextArea
                                                                    placeholder="Quartier, ville…"
                                                                    value={formData.adresseResidence}
                                                                    onChange={setE("adresseResidence")}
                                                                    rows={2}
                                                                    style={{ borderRadius: 8 }}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={24}>
                                                            <Form.Item
                                                                label={<span style={FIELD_LABEL}>Statut du compte</span>}
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: 10,
                                                                        padding: "10px 14px",
                                                                        background: formData.active ? "#f6ffed" : "#fafafa",
                                                                        borderRadius: 8,
                                                                        border: `1px solid ${formData.active ? "#b7eb8f" : "#f0f0f0"}`,
                                                                        transition: "all 0.2s",
                                                                    }}
                                                                >
                                                                    <Switch
                                                                        checked={formData.active}
                                                                        onChange={set("active")}
                                                                        checkedChildren="Actif"
                                                                        unCheckedChildren="Inactif"
                                                                    />
                                                                    <Text
                                                                        style={{
                                                                            fontSize: 13,
                                                                            color: formData.active ? "#52c41a" : "#8c8c8c",
                                                                        }}
                                                                    >
                                                                        {formData.active
                                                                            ? "Le compte sera actif dès la création"
                                                                            : "Le compte sera inactif à la création"}
                                                                    </Text>
                                                                </div>
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                </Form>

                                                <div
                                                    style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}
                                                >
                                                    {isCourtier ? (
                                                        <PrimaryButton
                                                            icon={<SaveOutlined />}
                                                            loading={loading}
                                                            onClick={async () => {
                                                                if (validateStep1()) {
                                                                    const exists = await checkEmailExists();
                                                                    if (!exists) confirmAndSubmit();
                                                                }
                                                            }}
                                                        >
                                                            Enregistrer
                                                        </PrimaryButton>
                                                    ) : (
                                                        <PrimaryButton onClick={handleNext}>
                                                            Suivant <ArrowRightOutlined />
                                                        </PrimaryButton>
                                                    )}
                                                </div>
                                            </>
                                        )}

                                        {/* ─── Étape 2 : Profil & Accès ─── */}
                                        {currentStep === 1 && (
                                            <>
                                                <StepHeader
                                                    icon={<SafetyCertificateOutlined style={{ color: "#722ed1", fontSize: 18 }} />}
                                                    bg="#f0f0ff"
                                                    title="Profil & Accès"
                                                    subtitle="Définissez les droits et l'affectation de l'utilisateur"
                                                />

                                                <Form layout="vertical" requiredMark={false} component="div">
                                                    <div style={SECTION_LABEL_STYLE}>
                                                        <SafetyCertificateOutlined /> Profil
                                                        {REQ}
                                                    </div>

                                                    {/* Recherche : filtre par nom de profil ou raison sociale de la compagnie.
                                                        Affichée dès qu'il y a beaucoup de profils (ex. connexion Pool). */}
                                                    {profils.length > 8 && (
                                                        <Input
                                                            allowClear
                                                            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                                                            placeholder="Rechercher un profil ou une compagnie…"
                                                            value={profilSearch}
                                                            onChange={(e) => setProfilSearch(e.target.value)}
                                                            style={{ marginBottom: 12, maxWidth: 420 }}
                                                        />
                                                    )}

                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                                                            gap: 12,
                                                            marginBottom: 20,
                                                        }}
                                                    >
                                                        {filteredProfils.map((pro) => (
                                                            <SelectCard
                                                                key={pro.codeId}
                                                                selected={selectedProfil?.codeId === pro.codeId}
                                                                onClick={() => handleProfilSelect(pro.codeId)}
                                                                icon={<SafetyCertificateOutlined />}
                                                                title={pro.nom}
                                                                subtitle={pro.organisme?.raisonSociale}
                                                                accentColor="#1677ff"
                                                            />
                                                        ))}
                                                    </div>
                                                    {profils.length > 8 && filteredProfils.length === 0 && (
                                                        <Empty
                                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                            description="Aucun profil ne correspond à la recherche"
                                                            style={{ marginBottom: 20 }}
                                                        />
                                                    )}

                                                    {agences.length > 0 && (
                                                        <>
                                                            <Divider style={{ margin: "4px 0 16px" }} />
                                                            <div style={SECTION_LABEL_STYLE}>
                                                                <ApartmentOutlined /> Agence
                                                                {REQ}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                                                                    gap: 12,
                                                                    marginBottom: 20,
                                                                }}
                                                            >
                                                                {agences.map((ag) => (
                                                                    <SelectCard
                                                                        key={ag.codeId}
                                                                        selected={selectedAgence === ag.codeId}
                                                                        onClick={() => setSelectedAgence(ag.codeId)}
                                                                        icon={<ApartmentOutlined />}
                                                                        title={ag.nom}
                                                                        subtitle={ag.ville}
                                                                        accentColor="#52c41a"
                                                                        disabled={!!param?.codeAgence || isLoggedCourtier}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </Form>

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        marginTop: 8,
                                                    }}
                                                >
                                                    <Button
                                                        icon={<ArrowLeftOutlined />}
                                                        onClick={() => setCurrentStep(0)}
                                                        style={{ borderRadius: 8 }}
                                                    >
                                                        Précédent
                                                    </Button>
                                                    <PrimaryButton
                                                        icon={<SaveOutlined />}
                                                        loading={loading}
                                                        onClick={confirmAndSubmit}
                                                    >
                                                        Créer l'utilisateur
                                                    </PrimaryButton>
                                                </div>
                                            </>
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

/* ── Helpers de présentation ── */
const ConfirmRow = ({ icon, label, value }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 11, color: "#8c8c8c", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                {label}
            </span>
            <div style={{ fontSize: 13, color: "#262626", fontWeight: 500, marginTop: 1, wordBreak: "break-all" }}>
                {value}
            </div>
        </div>
    </div>
);

const StepHeader = ({ icon, bg, title, subtitle }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <div
            style={{
                width: 38, height: 38, borderRadius: 10,
                background: bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
            }}
        >
            {icon}
        </div>
        <div>
            <Text strong style={{ fontSize: 15, color: "#262626", display: "block" }}>
                {title}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
                {subtitle}
            </Text>
        </div>
    </div>
);

const PrimaryButton = ({ children, ...props }) => (
    <Button
        type="primary"
        style={{
            borderRadius: 8,
            fontWeight: 600,
            minWidth: 148,
            background: "linear-gradient(135deg, #242166, #3b82f6)",
            border: "none",
            boxShadow: "0 4px 12px rgba(36,33,102,0.25)",
        }}
        {...props}
    >
        {children}
    </Button>
);

export default NewAddUtilisateur;
